import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import { useAuth } from "@/lib/auth-context";
import type { PortalJourneyTaskComment } from "./types";

// POST /api/portal/journeys/progress/:progressId/comments — partner posts
// a comment on a journey task thread. Optimistic append (mirror MDF +
// bucket 3 deal-comment pattern): write to the cache immediately with a
// temp id, rollback on error, refetch on settle to land the canonical id.

type Input = { progressId: string; body: string };
type Context = {
  previous: PortalJourneyTaskComment[] | undefined;
  key: ReturnType<typeof queryKeys.portal.journeys.taskComments>;
};

export function useAddTaskComment() {
  const queryClient = useQueryClient();
  const { session } = useAuth();
  return useMutation<
    PortalJourneyTaskComment,
    ApiClientError,
    Input,
    Context
  >({
    mutationFn: ({ progressId, body }) =>
      apiFetch<PortalJourneyTaskComment>(
        `/portal/journeys/progress/${progressId}/comments`,
        {
          method: "POST",
          body: JSON.stringify({ body }),
        },
      ),
    onMutate: async ({ progressId, body }) => {
      const key = queryKeys.portal.journeys.taskComments(progressId);
      await queryClient.cancelQueries({ queryKey: key });
      const previous =
        queryClient.getQueryData<PortalJourneyTaskComment[]>(key);
      const optimistic: PortalJourneyTaskComment = {
        id: `temp-${crypto.randomUUID()}`,
        progress_id: progressId,
        author_user_id: session?.userId ?? null,
        author_role: "partner",
        body,
        created_at: new Date().toISOString(),
      };
      queryClient.setQueryData<PortalJourneyTaskComment[]>(key, (old) => [
        ...(old ?? []),
        optimistic,
      ]);
      return { previous, key };
    },
    onError: (err, _vars, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(context.key, context.previous);
      }
      toast.error(err.message ?? "Failed to post comment");
    },
    onSettled: (_data, _err, { progressId }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.portal.journeys.taskComments(progressId),
      });
    },
  });
}
